<template>
    <VJoyDialog id="removalExperienceConfirmDialog" ref="removeDialog" pattern-confirm :cancel-text="t('general.cancel')">
        <template #dialog-header>{{ t('generic.form.confirm.text') }}</template>
        <template #dialog-confirm>
            <VJoyButton variant="main" data-testid="remove-experience-confirm-button" @click="removeExperience">
                {{ $t('general.continue') }}
            </VJoyButton>
        </template>
    </VJoyDialog>
    <Draggable
        v-model="experiencesSectionState"
        class="profile-experiences__list"
        :group="{name: 'experiences', put: true, pull: true}"
        draggable=".draggable-experience"
        handle=".draggable-handle"
        tag="ul"
        @start="dragging = true"
        @end="dragging = false"
        @update="dragged"
    >
        <li
            v-for="element in experiencesSectionState"
            :id="anchorsPageProfile.experienceById(element.id!)"
            :key="`experience--${element.id}`"
            :value="element.index"
            :data-testid="`profile-experience-item--${element.index}`"
            class="profile-experiences__list-item draggable-experience"
        >
            <div class="profile-experiences__list-item-wrapper">
                <VJoyWrapper class="action-edit" column-gap="4" justify="space-between" wrap="nowrap" item-stretch no-margin>
                    <VJoyIconButton
                        :data-testid="`profile-experience-edit-${element.index}`"
                        variant="ghost"
                        size="small"
                        icon="pen"
                        :disabled="loading"
                        :label="$t('general.edit')"
                        @click="openExperiencesEditDialog(element.index, element.id)"
                    />
                    <!-- [SM-1429] We do not use VJoyDialogTrigger on purpose here, otherwise element.index never changes inside it -->
                    <VJoyIconButton
                        :data-testid="`profile-experience-delete--${element.index}`"
                        :disabled="loading || !props.isEnabledRemoval"
                        variant="ghost"
                        size="small"
                        icon="trash"
                        class="toolbar-remove"
                        :label="$t('general.delete')"
                        @click="openRemoveExperienceConfirmDialog(element.index)"
                    />
                    <VJoyIcon size="xsmall" name="menu" class="draggable-handle" />
                </VJoyWrapper>
                <ExperienceReadOnly
                    class="profile-experiences__list-item-content-edit"
                    data-testid="experience-read-only-item"
                    :experience="mapToExperienceItem(element)"
                    :title-margin-right="titleMarginRight"
                ></ExperienceReadOnly>
            </div>
        </li>
    </Draggable>
</template>

<script setup lang="ts">
    import {onUnmounted, useTemplateRef} from 'vue';
    import {VueDraggable as Draggable} from 'vue-draggable-plus';
    import {computed, navigateTo, onMounted, ref, storeToRefs, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {useDisplayMobile} from '@malt/utils';
    import {VJoyButton, VJoyIconButton, VJoyDialog, VJoyIcon, VJoyWrapper} from '@maltjoy/core-vue';
    import type {ExperienceItem} from '~/components/_common/experience/ExperienceItem';
    import ExperienceReadOnly from '~/components/_common/experience/ExperienceReadOnly.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import type {ExperienceRequest} from '~/types/infra/ExperienceRequest';
    import {useExperienceSectionState} from '../../composable/useExperienceSectionState';

    const props = defineProps<{
        isEnabledRemoval: boolean;
        isShowingAllExperiences: boolean;
    }>();

    const dragging = ref<boolean>(false);
    const isNewCompanyLogoEnabled = useFeatureFlag('new-company-logo');

    // Composables
    const {t} = useTranslation();

    // State
    const experiencesSectionState = useExperienceSectionState();

    // Stores
    const {reorderExperiences, deleteExperience, reloadExperiences} = useProfileStore();
    const {experiences} = storeToRefs(useProfileStore());

    // Refs
    const removeDialog = useTemplateRef('removeDialog');
    const idx = ref(0);
    const loading = ref(false);

    const EXPERIENCES_TO_DISPLAY_BY_DEFAULT = 3;

    const {isDisplayMobile} = useDisplayMobile('Xxs');
    const actionsToolbarWidth = 124;
    const titleMarginRight = computed(() => (!isDisplayMobile.value ? actionsToolbarWidth + 4 : 0));

    function mapToExperienceItem(xpRequest: ExperienceRequest): ExperienceItem {
        return {
            company: xpRequest.company,
            title: xpRequest.title,
            activityArea: xpRequest.area,
            location: xpRequest.location?.formattedAddress,
            startDate: xpRequest.startDate,
            endDate: xpRequest.endDate,
            currentJob: xpRequest.currentJob,
            description: xpRequest.description,
            skills: xpRequest.skills.map((skill) => skill.label),
            logoSrc: xpRequest.logo?.baseUrl
                ? `${xpRequest.logo.baseUrl}/${xpRequest.logo.name}`
                : isNewCompanyLogoEnabled.value
                  ? (xpRequest.logoUrl ?? undefined)
                  : undefined,
            missionId: xpRequest.missionId,
        };
    }

    onMounted(() => {
        eventBus.on('resume-import:updated', updateExperienceList);
    });

    onUnmounted(() => {
        eventBus.off('resume-import:updated', updateExperienceList);
    });

    async function updateExperienceList() {
        await reloadExperiences();
    }

    function preparePayloadAfterDrag(): string[] {
        const experienceRequestIds = experiencesSectionState.value.map((exp) => exp.id).filter((id): id is string => id !== undefined);
        if (!props.isShowingAllExperiences) {
            const hiddenExperiences = experiences.value?.items ?? [];

            return [
                ...experienceRequestIds,
                ...hiddenExperiences
                    .slice(EXPERIENCES_TO_DISPLAY_BY_DEFAULT)
                    .map((exp) => exp.id)
                    .filter((id): id is string => id !== undefined),
            ];
        }
        return experienceRequestIds;
    }

    async function dragged() {
        loading.value = true;
        if (experiencesSectionState.value.length > 0) {
            const payload = preparePayloadAfterDrag();
            await reorderExperiences(payload);
        }
        loading.value = false;
    }

    function openExperiencesEditDialog(idx: number, id: string | undefined) {
        return navigateTo({path: `/profile/experiences/${id}`, query: {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE}});
    }

    async function removeExperience() {
        const experienceToDelete = experiencesSectionState.value.at(idx.value);
        if (experienceToDelete && experienceToDelete.id) {
            await deleteExperience(experienceToDelete.id);
            removeDialog.value?.hide();
            eventBus.emit('experiences:updated');
        }
    }

    async function openRemoveExperienceConfirmDialog(index: number) {
        idx.value = index;
        await removeDialog.value?.show();
    }
</script>

<style lang="scss" scoped>
    .draggable-handle {
        cursor: grab;
        margin: var(--joy-core-spacing-3);

        // Tweak to ease the drag & drop on mobile
        @media screen and (max-width: 600px) {
            position: relative;

            &::after {
                content: '';
                position: absolute;
                inset: calc(-1 * var(--joy-core-spacing-3));
            }
        }
    }

    .action-edit {
        position: absolute;
        right: 0;

        @media screen and (max-width: 600px) {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-4);
        }
    }

    .toolbar-remove :deep(.joy-icon),
    .toolbar-remove:hover :deep(.joy-icon) {
        color: var(--joy-color-error-50) !important; // I know
    }

    .profile-experiences__list-item {
        &-content-edit {
            @media screen and (max-width: 600px) {
                justify-content: flex-start;
                min-height: 152px;
                width: 85%;
            }

            :deep(.profile-experience-item__content__title) {
                width: 75%;

                @media screen and (max-width: 600px) {
                    width: 100%;
                }
            }
        }
    }
</style>
